import { useCallback, useMemo } from 'react';

import { ShopOverlayRoutes, ShopProvider } from '@givebacks/shop';
import { ShopRoutes } from '@givebacks/shop/routes';
import { IfFeatureState_Core } from 'core';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { StoreProvider } from 'store';
import { useShowStoreCause } from 'store/Api';
import { CampaignSupporterProvider, CampaignsRoutes } from 'store/Crowdfunding';
import { notifications } from 'ui';

import { Map, MapApiProvider } from '../../components/Map/Map';
import { MapProvider } from '../../components/Map/context/Map.context';
import { ExploreCauses } from '../../pages/give/ExploreCauses/ExploreCauses';
import { About } from './About';
import { Campaigns } from './Campaigns';
import { CausePageLayout } from './CausePage';
import { Directory } from './Directory/Directory';
import { Events } from './Events';

const RedirectCauseDetail = () => {
  const { uuid } = useParams();
  return <Navigate to={`/causes/${uuid}`} replace={true} />;
};

const StoreProviderLayout = () => {
  const context = useOutletContext();
  return (
    <MapApiProvider>
      <MapProvider id={'events'}>
        {/* Provides access to the store atoms used with the store package */}
        <StoreProvider>
          {/* Provides access to the crowdfunding atoms used with the crowdfunding package */}
          <CampaignSupporterProvider>
            <Outlet context={context} />
          </CampaignSupporterProvider>
        </StoreProvider>
      </MapProvider>
    </MapApiProvider>
  );
};

const CausePageStoreLayout = () => {
  const { uuid } = useParams();
  const { data: storeCause } = useShowStoreCause({ causeId: uuid });

  const notFoundRedirect = useCallback(
    () => ({
      to: uuid ? `/causes/${uuid}` : '/causes',
      replace: true,
      action: () =>
        void notifications.show({
          variant: 'error',
          message: 'No longer available',
        }),
    }),
    [uuid]
  );

  if (!uuid || !storeCause) return null;

  return (
    <ShopProvider
      router={{
        basePath: `/causes/${uuid}`,
        useNavigate: useNavigate,
        useLocation: useLocation,
      }}
      getStoreId={() => uuid}
      storeFrontOptions={{
        showTitle: false,
        showBanner: false,
        showCategoryButtons: true,
        disableStorePageBackButton: true,
        storePageContainerProps: {
          px: 0,
        },
      }}
      notFoundRedirect={notFoundRedirect}
      isConsolidatedCheckoutEnabled={true}
    >
      <Outlet />
      {/* This is used to mount the shop overlay routes at the cause page level */}
      <ShopOverlayRoutes basePath={`/causes/${uuid}`} />
    </ShopProvider>
  );
};

const CausePageStoreRoutes = () => {
  return (
    <>
      <Routes>
        <Route path="*" element={<ShopRoutes />} />
      </Routes>
    </>
  );
};

const CausePageRoutes = () => {
  const { uuid } = useParams();

  const basePath = useMemo(() => `/causes/${uuid}`, [uuid]);

  return (
    <Routes>
      <Route element={<CausePageStoreLayout />}>
        <Route element={<StoreProviderLayout />}>
          <Route element={<CausePageLayout />}>
            <Route path="about" element={<About />} />
            <Route path="shop" element={<CausePageStoreRoutes />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="events" element={<Events />} />
            <Route
              path="directory"
              element={
                <IfFeatureState_Core featureId="internalPreview">
                  <Directory />
                </IfFeatureState_Core>
              }
            />
            <Route index element={<About />} />
          </Route>
          <Route path="shop/*" element={<ShopRoutes />} />

          <Route
            path="events/*"
            element={<CampaignsRoutes EventMap={Map} isEventRoute={true} basePath={basePath} />}
          />
          <Route path="campaigns/*" element={<CampaignsRoutes basePath={basePath} />} />

          <Route path="*" element={<Navigate to="about" replace />} />
        </Route>
      </Route>
    </Routes>
  );
};

const CauseRoutes = () => {
  const location = useLocation();
  const { state } = location as { state: { backgroundLocation?: typeof location } };
  const backgroundLocation = useMemo(() => state?.backgroundLocation || false, [state]);

  return (
    <Routes location={backgroundLocation || location}>
      <Route path=":uuid/*" element={<CausePageRoutes />} />

      <Route index element={<ExploreCauses />} />

      {/* Redirects */}
      <Route path="my-causes" element={<Navigate to="/causes" replace />} />
      <Route path="detail/:uuid" element={<RedirectCauseDetail />} />
    </Routes>
  );
};

export { CauseRoutes };
